import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Wheat, Store, Package, Cherry, Carrot, Leaf, MapPin, Navigation, Search } from 'lucide-react';

const DirectoryHero = () => {
  const [locationInput, setLocationInput] = useState('');
  const [isLocating, setIsLocating] = useState(false);

  const categories = [
    { id: 'farms', name: 'Farms', icon: Wheat, link: '/category/farms' },
    { id: 'markets', name: 'Markets', icon: Store, link: '/category/farmers-markets' },
    { id: 'csa', name: 'CSA', icon: Package, link: '/category/csa-programs' },
    { id: 'upick', name: 'U-Pick', icon: Cherry, link: '/category/u-pick' },
    { id: 'farmstands', name: 'Farm Stands', icon: Carrot, link: '/category/farm-stands' },
    { id: 'organic', name: 'Organic', icon: Leaf, link: '/category/organic-producers' },
  ];

  const handleGeolocation = useCallback(() => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setIsLocating(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await response.json();
          const city = data.address?.city || data.address?.town || data.address?.village || '';
          const state = data.address?.state || '';
          const zip = data.address?.postcode || '';
          setLocationInput(city && state ? `${city}, ${state} ${zip}`.trim() : `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
        } catch (error) {
          console.error('Error getting location name:', error);
          setLocationInput(`${position.coords.latitude.toFixed(4)}, ${position.coords.longitude.toFixed(4)}`);
        } finally {
          setIsLocating(false);
        }
      },
      (error) => {
        console.error('Error getting location:', error);
        alert('Unable to retrieve your location. Please enter it manually.');
        setIsLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (locationInput.trim()) {
      window.location.href = `/browse?location=${encodeURIComponent(locationInput)}`;
    }
  };

  const publicUrl = process.env.PUBLIC_URL + '/';

  return (
    <>
      {/* Start Directory Hero */}
      <section className="directory-hero">
        <div
          className="directory-hero__bg"
          style={{
            backgroundImage: `url(${publicUrl}assets/images/backgrounds/directory-hero-bg.jpg)`,
          }}
        ></div>
        <div className="directory-hero__overlay"></div>

        <div className="container">
          <div className="directory-hero__content text-center">
            {/* Headline */}
            <div className="directory-hero__title wow fadeInUp" data-wow-delay="0ms" data-wow-duration="1000ms">
              <h1>Find Fresh, Local Food Near You</h1>
            </div>

            {/* Subheadline */}
            <div className="directory-hero__text wow fadeInUp" data-wow-delay="100ms" data-wow-duration="1000ms">
              <p>Discover farms, farmers markets, and CSA programs in your community</p>
            </div>

            {/* Search Box */}
            <div className="directory-hero__search-box wow fadeInUp" data-wow-delay="200ms" data-wow-duration="1000ms">
              <form onSubmit={handleSubmit} className="directory-hero__search-form">
                <div className="directory-hero__search-wrapper">
                  <div className="directory-hero__input-wrapper">
                    <MapPin className="directory-hero__input-icon" size={20} />
                    <input
                      type="text"
                      placeholder="Enter city, state or zip code"
                      value={locationInput}
                      onChange={(e) => setLocationInput(e.target.value)}
                      className="directory-hero__input"
                    />
                  </div>

                  <button
                    type="button"
                    className="directory-hero__geolocation-btn"
                    onClick={handleGeolocation}
                    disabled={isLocating}
                    title="Use my location"
                  >
                    <Navigation
                      className={`directory-hero__nav-icon ${isLocating ? 'directory-hero__nav-icon--locating' : ''}`}
                      size={18}
                    />
                    <span className="directory-hero__geolocation-text">
                      {isLocating ? 'Locating...' : 'Use my location'}
                    </span>
                  </button>

                  <button type="submit" className="directory-hero__search-btn thm-btn">
                    <Search size={18} />
                    <span>Find Local Food</span>
                  </button>
                </div>
              </form>
            </div>

            {/* Category Links */}
            <div className="directory-hero__categories wow fadeInUp" data-wow-delay="300ms" data-wow-duration="1000ms">
              <div className="directory-hero__categories-title">
                <h4>Browse by Category</h4>
              </div>
              <ul className="directory-hero__categories-list">
                {categories.map((category, index) => {
                  const IconComponent = category.icon;
                  return (
                    <li
                      key={category.id}
                      className="directory-hero__categories-item wow fadeInUp"
                      data-wow-delay={`${400 + index * 50}ms`}
                      data-wow-duration="800ms"
                    >
                      <Link to={process.env.PUBLIC_URL + category.link} className="directory-hero__category-link">
                        <div className="directory-hero__category-icon">
                          <IconComponent size={28} strokeWidth={1.5} />
                        </div>
                        <span className="directory-hero__category-name">{category.name}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </section>
      {/* End Directory Hero */}

      <style>{`
        /* Directory Hero Section */
        .directory-hero {
          position: relative;
          display: block;
          min-height: 600px;
          padding: 120px 0 100px;
          overflow: hidden;
        }

        @media (min-width: 768px) {
          .directory-hero {
            min-height: 700px;
            padding: 160px 0 120px;
          }
        }

        .directory-hero__bg {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center center;
          background-repeat: no-repeat;
          z-index: 1;
        }

        .directory-hero__overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            180deg,
            rgba(45, 90, 39, 0.75) 0%,
            rgba(44, 62, 45, 0.85) 100%
          );
          z-index: 2;
        }

        .directory-hero__content {
          position: relative;
          z-index: 3;
        }

        /* Title */
        .directory-hero__title {
          margin-bottom: 16px;
        }

        .directory-hero__title h1 {
          color: #ffffff;
          font-size: 32px;
          line-height: 1.2;
          font-weight: 700;
          font-family: var(--thm-font-2);
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        @media (min-width: 768px) {
          .directory-hero__title h1 {
            font-size: 48px;
            line-height: 1.15;
          }
        }

        @media (min-width: 992px) {
          .directory-hero__title h1 {
            font-size: 56px;
          }
        }

        /* Subheadline */
        .directory-hero__text {
          margin-bottom: 40px;
        }

        .directory-hero__text p {
          color: rgba(255, 255, 255, 0.9);
          font-size: 16px;
          line-height: 1.6;
          max-width: 600px;
          margin: 0 auto;
        }

        @media (min-width: 768px) {
          .directory-hero__text p {
            font-size: 18px;
          }
        }

        /* Search Box */
        .directory-hero__search-box {
          max-width: 700px;
          margin: 0 auto 50px;
        }

        .directory-hero__search-form {
          width: 100%;
        }

        .directory-hero__search-wrapper {
          display: flex;
          flex-direction: column;
          gap: 12px;
          background: #ffffff;
          padding: 16px;
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
        }

        @media (min-width: 768px) {
          .directory-hero__search-wrapper {
            flex-direction: row;
            align-items: center;
            padding: 8px 8px 8px 20px;
            border-radius: 50px;
            gap: 8px;
          }
        }

        .directory-hero__input-wrapper {
          position: relative;
          flex: 1;
          display: flex;
          align-items: center;
        }

        .directory-hero__input-icon {
          color: var(--thm-base);
          margin-right: 12px;
          flex-shrink: 0;
        }

        .directory-hero__input {
          width: 100%;
          border: none;
          outline: none;
          font-size: 16px;
          color: var(--thm-black);
          background: transparent;
          padding: 12px 0;
          font-family: var(--thm-font);
        }

        .directory-hero__input::placeholder {
          color: var(--thm-gray);
        }

        @media (min-width: 768px) {
          .directory-hero__input {
            padding: 8px 0;
          }
        }

        /* Geolocation Button */
        .directory-hero__geolocation-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 10px 16px;
          background: var(--thm-bg-sage);
          border: none;
          border-radius: 8px;
          color: var(--thm-base);
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        @media (min-width: 768px) {
          .directory-hero__geolocation-btn {
            border-radius: 25px;
          }
        }

        .directory-hero__geolocation-btn:hover {
          background: var(--thm-base);
          color: #ffffff;
        }

        .directory-hero__geolocation-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .directory-hero__nav-icon {
          transition: transform 0.3s ease;
        }

        .directory-hero__nav-icon--locating {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .directory-hero__geolocation-text {
          display: none;
        }

        @media (min-width: 576px) {
          .directory-hero__geolocation-text {
            display: inline;
          }
        }

        /* Search Button */
        .directory-hero__search-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 14px 28px;
          background: var(--thm-accent);
          border: none;
          border-radius: 8px;
          color: #ffffff;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        @media (min-width: 768px) {
          .directory-hero__search-btn {
            border-radius: 50px;
            padding: 14px 32px;
          }
        }

        .directory-hero__search-btn:hover {
          background: var(--thm-base);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(45, 90, 39, 0.3);
        }

        /* Categories Section */
        .directory-hero__categories {
          max-width: 800px;
          margin: 0 auto;
        }

        .directory-hero__categories-title {
          margin-bottom: 24px;
        }

        .directory-hero__categories-title h4 {
          color: rgba(255, 255, 255, 0.85);
          font-size: 14px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 1.5px;
        }

        .directory-hero__categories-list {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 12px;
          padding: 0;
          margin: 0;
          list-style: none;
        }

        @media (min-width: 576px) {
          .directory-hero__categories-list {
            gap: 16px;
          }
        }

        .directory-hero__categories-item {
          flex: 0 0 auto;
        }

        .directory-hero__category-link {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 16px 20px;
          background: rgba(255, 255, 255, 0.12);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          color: #ffffff;
          text-decoration: none;
          transition: all 0.3s ease;
          min-width: 90px;
        }

        @media (min-width: 576px) {
          .directory-hero__category-link {
            padding: 20px 24px;
            min-width: 100px;
          }
        }

        .directory-hero__category-link:hover {
          background: rgba(255, 255, 255, 0.95);
          color: var(--thm-base);
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
          border-color: transparent;
        }

        .directory-hero__category-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          transition: transform 0.3s ease;
        }

        .directory-hero__category-link:hover .directory-hero__category-icon {
          transform: scale(1.1);
        }

        .directory-hero__category-name {
          font-size: 13px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        @media (min-width: 576px) {
          .directory-hero__category-name {
            font-size: 14px;
          }
        }
      `}</style>
    </>
  );
};

export default DirectoryHero;
